import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectWhitelabelTheme } from 'selectors/whitelabelSelectors';

/**
 * useWhitelabelTheme hook. Return whitelabel theme configuration and utilities from Redux state.
 * @returns {object} Theme configuration including getServiceType helper.
 */
const useWhitelabelTheme = () => {
  const { t } = useTranslation();
  const whitelabelTheme = useSelector(selectWhitelabelTheme);
  /**
   * Get service type icon and label
   * @param {*} service - Service type
   * @returns - Icon and label
   */
  const getServiceType = (service) => {
    if (!whitelabelTheme.serviceTypes) {
      // If the translation key not found, just use service as the label
      const translationKey = `type.${service}`;
      const attemptedLabel = t(`trips:${translationKey}`);
      const label = attemptedLabel === translationKey ? service : attemptedLabel;
      return { icon: 'Bus', label };
    }
    return {
      icon:
        whitelabelTheme.serviceTypes[service]?.icon ||
        whitelabelTheme.serviceTypes.default?.icon ||
        'Bus',
      label:
        whitelabelTheme.serviceTypes[service]?.label ||
        whitelabelTheme.serviceTypes.default?.label ||
        service,
      hasMultiplePricing:
        whitelabelTheme.serviceTypes[service]?.hasMultiplePricing ||
        whitelabelTheme.serviceTypes.default?.hasMultiplePricing ||
        false,
    };
  };

  return {
    ...whitelabelTheme,
    purchaseStepsIcons: whitelabelTheme.purchaseStepsIcons || {},
    amenities: whitelabelTheme.amenities || {},
    paymentStatusIcons: whitelabelTheme.paymentStatusIcons || {},
    resultBoxStyle: whitelabelTheme.resultBoxStyle || '',
    getServiceType,
    resultsCTAColor: whitelabelTheme.resultsCTAColor || 'primary',
    // Fall back to `colors.headerText` before assuming white: that is the key
    // brands actually set to declare their header text color, and it is what
    // Header, DesktopHeader, OverlayView and Purchase already read directly.
    // Without it, a light-header brand renders white text on a white header.
    headerTextColor:
      whitelabelTheme.headerTextColor || whitelabelTheme.colors?.headerText || 'white',
    // Same dual-key shape as headerTextColor above: MainButton reads
    // `colors.mainButtonColor` directly, so nothing consumes this field today.
    // Kept consistent so it does not become the next white-on-white trap.
    mainButtonColor:
      whitelabelTheme.mainButtonColor || whitelabelTheme.colors?.mainButtonColor || 'white',
  };
};

export default useWhitelabelTheme;
